import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { ArchivesApi, ArchivesStatus } from "src/services/Api/Organization/types";
import useFlash from "go-alert/AlertMessage";
import FormatResourceStatusLabel from "go-app/components/FormatResourceStatus/FormatResourceStatus";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { getArchiveStatusClassName } from "../../pages/Index";
import { waitForArchiveStatusChange } from "../../utils";
import { ArchiveReport } from "./ArchiveReport";

interface Props {
	archiveInfo: ArchivesApi;
	setArchiveInfo: (archiveInfo: ArchivesApi) => void;
}

export const ArchivePreview = ({ archiveInfo, setArchiveInfo }: Props) => {
	const { t } = useTranslation();
	const { date, status, id } = archiveInfo;
	const archiveInfoFormattedDate = FormatDate(date);
	const { isMobile } = useWindowSize();
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const displayMobileActions = status !== "CLOSED";

	const removeArchiveHandler = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await apiOrganization.removeArchive(id);
			addSuccessFlash(t("lib:common.flash.completed"));
			history.push(`/${organization.id}/settings/archives`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const updateArchiveHandler = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const updatedArchiveInfo = await apiOrganization.updateArchive(id);
			setArchiveInfo(updatedArchiveInfo);

			const newStatus = await waitForArchiveStatusChange(
				updatedArchiveInfo.id,
				(updatedStatus: ArchivesStatus) => updatedStatus === "OPEN" || updatedStatus === "CLOSED"
			);
			if (newStatus) setArchiveInfo({ ...updatedArchiveInfo, status: newStatus });

			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const closeArchiveHandler = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const updatedArchiveInfo = await apiOrganization.closeArchive(id);
			setArchiveInfo(updatedArchiveInfo);

			const newStatus = await waitForArchiveStatusChange(
				updatedArchiveInfo.id,
				(updatedStatus: ArchivesStatus) => updatedStatus === "CLOSED"
			);
			if (newStatus) setArchiveInfo({ ...updatedArchiveInfo, status: newStatus });

			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.remove", { ns: "lib" }),
			action: removeArchiveHandler,
			hide: status === "CLOSED",
			dropdown: true,
		},
		{
			title: t("common.action.update"),
			action: updateArchiveHandler,
			hide: status === "CLOSED",
			dropdown: true,
		},
		{
			title: t("common.action.close", { ns: "lib" }),
			action: closeArchiveHandler,
			variant: "primary",
		},
	];

	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.remove", { ns: "lib" }),
			action: removeArchiveHandler,
			hide: status === "CLOSED",
		},
		{
			title: t("common.action.update"),
			action: updateArchiveHandler,
			hide: status === "CLOSED",
		},
		{
			title: t("common.action.close", { ns: "lib" }),
			action: closeArchiveHandler,
			hide: status === "CLOSED",
		},
	];

	const getHeaderTitle = () => {
		return (
			<>
				{`${t("modules.archives.header.preview.title")} ${archiveInfoFormattedDate}`}
				<FormatResourceStatusLabel status={status} className={getArchiveStatusClassName(status)} />
			</>
		);
	};

	return (
		<>
			{displayMobileActions && <MobileActions actions={mobileActions} />}
			<Header title={getHeaderTitle()} buttons={status !== "OPEN" || isMobile ? undefined : buttons} />
			<ArchiveReport />
		</>
	);
};
